import type { DialogProps } from '@radix-ui/react-dialog';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { Command as CommandPrimitive, useCommandState } from 'cmdk';
import { Search } from 'lucide-react';
import {
	type ComponentPropsWithoutRef,
	type HTMLAttributes,
	type ReactElement,
	type MouseEvent as ReactMouseEvent,
	type ReactNode,
	useEffect,
	useRef,
} from 'react';

import { Dialog, DialogContent } from '~/components/shadcn/dialog/motion-dialog';
import { cn } from '~/utils/cn.ts';

import type { OverlayTransitions } from './utils/animations';

function Command({
	className,
	shouldFilter,
	shouldSort: _shouldSort,
	...props
}: ComponentPropsWithoutRef<typeof CommandPrimitive> & {
	shouldSort?: boolean;
}) {
	return (
		<CommandPrimitive
			className={cn(
				// Lyra style: sharp corners
				'flex h-full w-full flex-col overflow-hidden rounded-none bg-popover text-popover-foreground',
				className,
			)}
			data-slot="command"
			shouldFilter={shouldFilter}
			{...props}
		/>
	);
}

interface CommandDialogProps extends DialogProps {
	classNames?: {
		command?: string;
		dialogContent?: string;
		portal?: string;
		overlay?: string;
	};
	defaultValue?: string;
	// biome-ignore lint/suspicious/noExplicitAny: Radix UI event type compatibility
	onPointerDownOutside?: (event: any) => void;
	shouldFilter?: boolean;
	shouldSort?: boolean;
	transition?: OverlayTransitions;
}

const CommandDialog = ({
	children,
	shouldFilter = true,
	shouldSort = true,
	defaultValue,
	classNames,
	transition = 'fromLeft',
	onPointerDownOutside,
	open,
	...props
}: CommandDialogProps) => {
	return (
		<Dialog open={open} {...props}>
			<DialogContent
				classNames={{
					content: cn('overflow-hidden bg-popover p-0 shadow-2xl', 'border-border', classNames?.dialogContent),
					overlay: classNames?.overlay,
					portal: cn('sm:pt-[10%]', classNames?.portal),
				}}
				isCloseable={false}
				onPointerDownOutside={onPointerDownOutside}
				open={open}
				transition={transition}
			>
				<Command
					className={cn(
						'[&_[cmdk-group-heading]]:text-foreground',
						'[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium',
						'[&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2',
						'[&_[cmdk-input]]:h-12',
						'[&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5',
						'[&_[cmdk-input-wrapper]_svg]:text-foreground',
						'[&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5',
						classNames?.command,
					)}
					defaultValue={defaultValue}
					shouldFilter={shouldFilter}
					shouldSort={shouldSort}
				>
					{children}
				</Command>
			</DialogContent>
		</Dialog>
	);
};

function CommandInput({
	className,
	containerClassName,
	CustomComponent,
	loading: _loading,
	...props
}: ComponentPropsWithoutRef<typeof CommandPrimitive.Input> & {
	CustomComponent?: ReactElement;
	containerClassName?: string;
	loading?: boolean;
}) {
	return (
		<div
			className={cn('flex items-center border-border border-b px-3', containerClassName)}
			cmdk-input-wrapper=""
			data-slot="command-input"
		>
			<Search className="mr-2 size-4 shrink-0 text-muted-foreground" />
			<CommandPrimitive.Input
				className={cn(
					'text-foreground',
					'placeholder:text-muted-foreground',
					// Lyra style: sharp corners, text-xs
					'flex h-10 w-full rounded-none bg-transparent',
					'py-3 text-xs outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
					className,
				)}
				{...props}
			/>{' '}
			{CustomComponent}
		</div>
	);
}

function CommandList({ className, ...props }: ComponentPropsWithoutRef<typeof CommandPrimitive.List>) {
	return (
		<CommandPrimitive.List
			className={cn('max-h-[300px] overflow-y-auto overflow-x-hidden', className)}
			data-slot="command-list"
			{...props}
		/>
	);
}

function CommandEmpty({ className, ...props }: ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>) {
	return (
		<CommandPrimitive.Empty
			// Lyra style: text-xs
			className={cn('py-6 text-center text-muted-foreground text-xs', className)}
			data-slot="command-empty"
			{...props}
		/>
	);
}

function CustomCommandGroup({
	children,
	className,
	customchildclassname,
	...props
}: ComponentPropsWithoutRef<typeof CommandPrimitive.Group> & {
	customchildclassname?: string;
}) {
	return (
		<CommandPrimitive.Group
			className={cn(
				'overflow-hidden p-1 text-foreground',
				'[&_[cmdk-group-heading]]:text-muted-foreground',
				'[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-xs',
				className,
			)}
			data-slot="custom-command-group"
			{...props}
		>
			<div className={customchildclassname}>{children}</div>
		</CommandPrimitive.Group>
	);
}

function CommandGroup({ className, ...props }: ComponentPropsWithoutRef<typeof CommandPrimitive.Group>) {
	return (
		<CommandPrimitive.Group
			className={cn(
				'overflow-hidden p-1 text-foreground',
				'[&_[cmdk-group-heading]]:text-muted-foreground',
				'[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-xs',
				className,
			)}
			data-slot="command-group"
			{...props}
		/>
	);
}

function CommandSeparator({ className, ...props }: ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>) {
	return (
		<CommandPrimitive.Separator
			className={cn('-mx-1 h-px bg-border', className)}
			data-slot="command-separator"
			{...props}
		/>
	);
}

interface CustomCommandItemProps extends ComponentPropsWithoutRef<typeof CommandPrimitive.Item> {
	readonly children?: ReactNode;
	readonly href?: string;
	readonly preloadDelay?: number;
}

function CustomCommandItem({ className, href, onSelect, preloadDelay = 300, value, ...props }: CustomCommandItemProps) {
	const navigate = useNavigate();
	const router = useRouter();
	const selectedValue = useCommandState((state) => state.value);
	const skipNextSelectRef = useRef(false);
	const normalizedValue = typeof value === 'string' ? value.trim() : undefined;
	const isSelected = normalizedValue != null && selectedValue === normalizedValue;

	useEffect(() => {
		if (!href || !isSelected) {
			return;
		}

		const timeout = window.setTimeout(() => {
			void router.preloadRoute({ to: href });
		}, preloadDelay);

		return () => {
			window.clearTimeout(timeout);
		};
	}, [href, isSelected, preloadDelay, router]);

	function navigateWithFallback(targetHref: string): void {
		if (typeof window === 'undefined') {
			void navigate({ to: targetHref });
			return;
		}

		const currentLocation = `${window.location.pathname}${window.location.search}${window.location.hash}`;

		void navigate({ to: targetHref });

		window.setTimeout(() => {
			const nextLocation = `${window.location.pathname}${window.location.search}${window.location.hash}`;

			if (nextLocation === currentLocation) {
				window.location.assign(targetHref);
			}
		}, 150);
	}

	function activateItem(selectedItemValue: string): void {
		onSelect?.(selectedItemValue);

		if (!href || onSelect) {
			return;
		}

		navigateWithFallback(href);
	}

	function handleSelect(selectedItemValue: string): void {
		if (skipNextSelectRef.current) {
			skipNextSelectRef.current = false;
			return;
		}

		activateItem(selectedItemValue);
	}

	function handleMouseDown(event: ReactMouseEvent<HTMLElement>): void {
		if (event.button !== 0 || event.defaultPrevented) {
			return;
		}

		const selectedItemValue = normalizedValue ?? event.currentTarget.textContent?.trim();

		if (!selectedItemValue) {
			return;
		}

		event.preventDefault();
		skipNextSelectRef.current = true;
		activateItem(selectedItemValue);
	}

	if (href) {
		return (
			<CommandPrimitive.Item
				asChild
				className={cn(
					'aria-selected:bg-accent aria-selected:text-accent-foreground',
					'hover:bg-accent hover:text-accent-foreground',
					'relative flex cursor-pointer select-none items-center rounded-none',
					'px-2 py-1.5 text-xs outline-hidden',
					'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
					className,
				)}
				data-slot="custom-command-item"
				onSelect={handleSelect}
				value={value}
				{...props}
			>
				<a
					href={href}
					onClick={(event) => {
						event.preventDefault();
					}}
					onMouseDown={handleMouseDown}
				>
					{props.children}
				</a>
			</CommandPrimitive.Item>
		);
	}

	return (
		<CommandPrimitive.Item
			className={cn(
				'aria-selected:bg-accent aria-selected:text-accent-foreground',
				'hover:bg-accent hover:text-accent-foreground',
				'relative flex cursor-pointer select-none items-center rounded-none',
				'px-2 py-1.5 text-xs outline-hidden',
				'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
				className,
			)}
			data-slot="custom-command-item"
			onMouseDown={handleMouseDown}
			onSelect={handleSelect}
			value={value}
			{...props}
		/>
	);
}

function CommandItem({ className, ...props }: ComponentPropsWithoutRef<typeof CommandPrimitive.Item>) {
	return (
		<CommandPrimitive.Item
			className={cn('data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50', className)}
			data-slot="command-item"
			{...props}
		/>
	);
}

function CommandShortcut({ className, ...props }: HTMLAttributes<HTMLSpanElement>) {
	return (
		<span
			className={cn('ml-auto text-muted-foreground text-xs tracking-widest', className)}
			data-slot="command-shortcut"
			{...props}
		/>
	);
}

export {
	Command,
	CommandDialog,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
	CommandShortcut,
	CustomCommandGroup,
	CustomCommandItem,
};
